import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useSafetyFacilityRoundsStore,
  ChecklistItem,
} from "../../../store/SafetyFacilityRoundsStore";
import { PageLoader } from "../../../components/loader/Loader";
import {
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  XCircle,
  ChevronRight,
  Save,
  X,
} from "lucide-react";
import { message } from "antd";

const ChecklistExecution: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { checklists, fetchChecklists, submitRoundExecution } =
    useSafetyFacilityRoundsStore();
  const [currentChecklist, setCurrentChecklist] = useState<any>(null);
  const [answers, setAnswers] = useState<Record<string, any>>({});
  const [submitting, setSubmitting] = useState(false);
  const [startTime] = useState<string>(new Date().toISOString());

  useEffect(() => {
    if (checklists.length === 0) {
      fetchChecklists({});
    }
  }, [fetchChecklists, checklists.length]);

  useEffect(() => {
    if (id && checklists.length > 0) {
      const found = checklists.find((c) => c._id === id);
      if (found) {
        setCurrentChecklist(found);
      }
    }
  }, [id, checklists]);

  const handleAnswer = (itemId: string, value: any) => {
    setAnswers((prev) => ({
      ...prev,
      [itemId]: value,
    }));
  };

  const handleRaiseIssue = (item: ChecklistItem) => {
    // Construct location object (mock/derived from context)
    const locationData = {
      premises: "Main Building", // In a real app, this comes from the assigned round context
      region: "North Region",
      area: "Floor 1",
    };

    navigate(
      `/safety-facility-rounds/issues/create?linkedChecklistItemId=${item.id}&description=${encodeURIComponent(
        item.questionText,
      )}&location=${encodeURIComponent(JSON.stringify(locationData))}`,
    );
  };

  const handleCompleteRound = async () => {
    setSubmitting(true);
    try {
      // Format answers for API
      const responses = Object.entries(answers).map(([itemId, value]) => ({
        itemId,
        value,
        // photos: [] // TODO: Add photo support if needed
      }));

      await submitRoundExecution({
        checklistId: currentChecklist._id,
        responses,
        startedAt: startTime,
        completedAt: new Date().toISOString(),
      });

      message.success("Round completed successfully!");
      setTimeout(() => {
        navigate(-1);
      }, 1000);
    } catch (error) {
      message.error("Failed to complete round. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!currentChecklist) {
    return (
      <div className="flex justify-center items-center h-screen">
        <PageLoader />
      </div>
    );
  }

  const progress =
    Math.round(
      (Object.keys(answers).length / currentChecklist.items.length) * 100,
    ) || 0;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4"
      style={{ zIndex: 9999 }}
    >
      <div className="bg-white w-full max-w-4xl max-h-[90vh] rounded-xl shadow-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-gray-800">
              {currentChecklist.name}
            </h2>
            <p className="text-gray-500 text-sm flex items-center gap-2 mt-1">
              {currentChecklist.category}
              <span className="w-1 h-1 bg-gray-300 rounded-full"></span>
              {currentChecklist.items.length} Questions
            </p>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
          >
            <X size={20} className="text-gray-500" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">
              Completion Progress
            </span>
            <span className="text-sm font-bold text-blue-600">{progress}%</span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2.5 overflow-hidden">
            <div
              className="bg-blue-600 h-2.5 rounded-full transition-all duration-500 ease-out"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>

        {/* Form Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="space-y-6">
            {currentChecklist.items.map(
              (item: ChecklistItem, index: number) => {
                const userAnswer = answers[item.id || index];

                return (
                  <div
                    key={item.id || index}
                    className={`bg-gray-50 rounded-xl p-6 transition-all ${
                      userAnswer !== undefined
                        ? "border-l-4 border-blue-500 shadow-sm"
                        : "border border-gray-200"
                    }`}
                  >
                    <div className="flex flex-col md:flex-row md:items-start gap-6">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-4">
                          <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-bold rounded-md">
                            Q{index + 1}
                          </span>
                          {item.isCritical && (
                            <span className="px-3 py-1 bg-red-100 text-red-700 text-sm font-bold rounded-md flex items-center gap-1">
                              <AlertTriangle size={14} />
                              Critical
                            </span>
                          )}
                        </div>

                        <p className="text-lg font-medium text-gray-900 mb-6 leading-relaxed">
                          {item.questionText}
                        </p>

                        {/* Answer Inputs */}
                        <div className="flex gap-4 items-center">
                          {item.responseType === "passfail" && (
                            <div className="flex gap-3">
                              <button
                                onClick={() =>
                                  handleAnswer(item.id || String(index), "pass")
                                }
                                className={`flex items-center gap-2 px-6 py-3 border rounded-lg transition-all font-medium ${
                                  userAnswer === "pass"
                                    ? "bg-green-600 text-white border-green-600 shadow-md transform scale-105"
                                    : "bg-white text-gray-600 hover:bg-green-50 hover:border-green-200 hover:text-green-700"
                                }`}
                              >
                                <CheckCircle size={18} />
                                Pass
                              </button>
                              <button
                                onClick={() => {
                                  handleAnswer(
                                    item.id || String(index),
                                    "fail",
                                  );
                                }}
                                className={`flex items-center gap-2 px-6 py-3 border rounded-lg transition-all font-medium ${
                                  userAnswer === "fail"
                                    ? "bg-red-600 text-white border-red-600 shadow-md transform scale-105"
                                    : "bg-white text-gray-600 hover:bg-red-50 hover:border-red-200 hover:text-red-700"
                                }`}
                              >
                                <XCircle size={18} />
                                Fail
                              </button>
                            </div>
                          )}

                          {item.responseType === "yesno" && (
                            <div className="flex gap-3">
                              <button
                                onClick={() =>
                                  handleAnswer(item.id || String(index), "yes")
                                }
                                className={`px-8 py-3 border rounded-lg transition-all font-medium ${
                                  userAnswer === "yes"
                                    ? "bg-blue-600 text-white border-blue-600 shadow-md"
                                    : "hover:bg-blue-50 text-gray-600 border-gray-200"
                                }`}
                              >
                                Yes
                              </button>
                              <button
                                onClick={() =>
                                  handleAnswer(item.id || String(index), "no")
                                }
                                className={`px-8 py-3 border rounded-lg transition-all font-medium ${
                                  userAnswer === "no"
                                    ? "bg-gray-800 text-white border-gray-800 shadow-md"
                                    : "hover:bg-gray-100 text-gray-600 border-gray-200"
                                }`}
                              >
                                No
                              </button>
                            </div>
                          )}

                          {/* Fallback for others */}
                          {!["passfail", "yesno"].includes(
                            item.responseType,
                          ) && (
                            <div className="text-gray-400 text-sm italic">
                              Input type '{item.responseType}' implementation
                              pending.
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="flex flex-col items-end justify-start gap-3">
                        <button
                          onClick={() => handleRaiseIssue(item)}
                          className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors border border-red-100"
                        >
                          <AlertTriangle size={16} />
                          Raise Issue
                        </button>
                      </div>
                    </div>
                  </div>
                );
              },
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={handleCompleteRound}
            disabled={submitting}
            className="flex items-center gap-2 px-8 py-3 bg-[#0fa9a6] text-white rounded-full font-semibold hover:bg-[#197f7d] transition-all"
          >
            {submitting ? (
              <>Processing...</>
            ) : (
              <>
                <Save size={20} />
                Complete Round
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ChecklistExecution;
